import React, {
  useContext,
  useState
} from 'react';
import { flowRight } from 'lodash';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import { ELSButton } from '@els/els-component-button-react';
import {
  ELSIcon,
} from '@els/els-component-form-field-react';
import { Icon } from '@els/els-react--icon';
import { AppContext } from '../../components/app/AppProvider.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import {
  getBookTitleWithEdition,
  getBrandName,
  getOrderedPurchasedEntitlements,
  getSelectedIsbnFilters
} from './ai-chat.utilities';
import { trackNewRelicAction } from '../../components/app/app-state-actions.utilities';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import { RecordEmpty } from '../../models/common.models';
import { ELSCheckBox } from '../../components/els.components';

export const AiChatBookFiltersModalId = 'AiChatBookFiltersModalId';

export type AiChatBookFiltersPropsOnly = RecordEmpty

export type AiChatBookFiltersProps = AiChatBookFiltersPropsOnly
  & ELSPropsFromToastService
  & ELSPropsFromModalService

export type AiChatBookFiltersState = {
  selectedIsbnMap: Record<string, boolean>;
}

const defaultState: AiChatBookFiltersState = {
  selectedIsbnMap: {}
};

const AiChatBookFiltersComponent = (props: AiChatBookFiltersProps) => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);

  const [state, setState] = useState<AiChatBookFiltersState>({
    ...defaultState,
    selectedIsbnMap: appState.selectedIsbnMap || defaultState.selectedIsbnMap
  });

  const entitlements = getOrderedPurchasedEntitlements(appState.entitlements);
  const userHasSherpathAiIsbns = entitlements.length > 0;
  const areAllBooksSelected = state.selectedIsbnMap && getSelectedIsbnFilters(state.selectedIsbnMap).length === entitlements.length;

  return (
    <div className="c-els-modal">
      <div className="c-els-modal__window c-els-modal__window--max-width">
        <div>
          <button
            type="button"
            className="c-els-modal__close"
            onClick={() => {
              props.modalService.closeModal(AiChatBookFiltersModalId);
            }}
          >
            <ELSIcon name="close" size="1x" />
            <span className="u-els-hide-visually">Close this modal window</span>
          </button>

          <h2>Choose books</h2>

          <div className="o-els-container">
            {!areAllBooksSelected && (
              <div className="c-els-banner c-els-banner--info">
                <div className="c-els-banner__icon">
                  <Icon
                    id="information-outline-circle"
                    sprite="InformationOutlineCircle"
                    size="s"
                    a11y={{ name: 'InformationOutlineCircle', description: 'InformationOutlineCircle' }}
                  />
                </div>
                <div className="c-els-banner__message">
                  While specific books are selected, {getBrandName(appState)} can only provide answers found in those books.
                  Add or remove books to refine your search.
                </div>
              </div>
            )}
            {areAllBooksSelected && (
              <div className="c-els-banner c-els-banner--alert u-els-margin-top">
                <div className="c-els-banner__icon">
                  <Icon
                    id="alert c-els-banner--alert"
                    sprite="Alert"
                    size="s"
                    a11y={{ name: 'Alert', description: 'Alert' }}
                  />
                </div>
                <div className="c-els-banner__message">
                  By default, {getBrandName(appState)} uses all of your books. Select fewer books to refine your search, or deselect all your books.
                </div>
              </div>
            )}
          </div>

          {userHasSherpathAiIsbns && (
            <div className="c-naa-ai-chat-book-filters__book-list">
              {
                entitlements.map(({ isbn, title, author, edition }) => {
                  if (!title) {
                    return null;
                  }
                  return (
                  <div
                    className="o-els-container"
                    key={isbn}
                  >
                    <ELSCheckBox
                      name="selectedBookOption"
                      id={`selectedBook_${isbn}`}
                      value={isbn}
                      checked={state.selectedIsbnMap[isbn]}
                      changeHandler={() => {
                          setState((prevState) => {
                            const newSelectedIsbnMap = {
                              ...prevState.selectedIsbnMap,
                              [isbn]: !prevState.selectedIsbnMap[isbn]
                            };
                            return {
                              selectedIsbnMap: newSelectedIsbnMap
                            };
                          });
                        }}
                    >
                      <div>{getBookTitleWithEdition(title, edition)}</div>
                      <div><small>{author}</small></div>
                    </ELSCheckBox>
                  </div>
                )})}
            </div>
          )}
          <div className="u-els-margin-top-2x">
            <FlexLayout modifiers={[FlexLayoutModifier.RIGHT, FlexLayoutModifier.GUTTERS]}>
              <FlexItem>
                <ELSButton
                  type="secondary"
                  preventAutoPending
                  onClick={() => {
                    trackNewRelicAction({
                      appStateRef,
                      actionProps: {
                        action: NewRelicAnalyticsAction.BOOK_FILTER_BTN_CLICK,
                        props: {
                          btn: 'RESET_BTN',
                          selectedIsbn: null
                        }
                      }
                    });
                    setState((prevState) => {
                      return {
                        ...prevState,
                        selectedIsbnMap: {}
                      };
                    });
                  }}>
                  Reset
                </ELSButton>
              </FlexItem>
              <FlexItem>
                <ELSButton
                  preventAutoPending
                  onClick={() => {
                    setAppState((prevState) => {
                      return {
                        ...prevState,
                        selectedIsbnMap: state.selectedIsbnMap
                      };
                    });
                    const selectedIsbns = getSelectedIsbnFilters(state.selectedIsbnMap);
                    trackNewRelicAction({
                      appStateRef,
                      actionProps: {
                        action: NewRelicAnalyticsAction.BOOK_FILTER_BTN_CLICK,
                        props: {
                          btn: 'APPLY_BTN',
                          selectedIsbn: selectedIsbns.join(',')
                        }
                      }
                    });
                    props.modalService.closeModal(AiChatBookFiltersModalId);
                  }}>
                  Apply
                </ELSButton>
              </FlexItem>
            </FlexLayout>
          </div>
        </div>
      </div>
    </div>
  );
};

const enhance = flowRight(
  ELSWithToastService,
  ELSWithModalService,
);

const AiChatBookFilters: React.FC<AiChatBookFiltersPropsOnly> = enhance(AiChatBookFiltersComponent as React.FC<AiChatBookFiltersProps>);
export default AiChatBookFilters;
